import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { SiteLogo } from '../ui/SiteLogo';
import { classNames } from '../../utils/classNames';
import { useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { selectDirection } from '../../redux/slices/appSlice';

const panelTransition = { type: 'spring', stiffness: 380, damping: 36 };

export const mobileMenuItemClass = (active = false) =>
  classNames(
    'flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-semibold transition-colors duration-200',
    active
      ? 'bg-accent text-white shadow-sm dark:text-slate-950'
      : 'text-text hover:bg-accent-soft/90'
  );

const staggerItem = {
  hidden: { opacity: 0, x: 16 },
  visible: (index) => ({
    opacity: 1,
    x: 0,
    transition: { delay: 0.04 + index * 0.045, duration: 0.28, ease: [0.22, 1, 0.36, 1] }
  })
};

const wrapWithStagger = (nodes) =>
  React.Children.toArray(nodes).map((child, index) => (
    <motion.div key={child.key ?? `mobile-menu-item-${index}`} custom={index} variants={staggerItem} initial="hidden" animate="visible">
      {child}
    </motion.div>
  ));

export const HeaderMobileMenu = ({
  open,
  onOpen,
  onClose,
  children,
  footer = null,
  panelClassName,
  brandTitle,
  brandSubtitle,
  hideTrigger = false,
  overlayClassName = 'lg:hidden'
}) => {
  const { t } = useI18n();
  const direction = useAppSelector(selectDirection);
  const location = useLocation();
  const title = brandTitle ?? t('appName');
  const subtitle = brandSubtitle ?? t('landingTopTag');
  const slideOffset = direction === 'rtl' ? '-100%' : '100%';

  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  const menuPortal =
    typeof document !== 'undefined'
      ? createPortal(
          <AnimatePresence>
            {open ? (
              <div key="header-mobile-menu-root" className={classNames('fixed inset-0 z-50', overlayClassName)}>
                <motion.button
                  type="button"
                  className="absolute inset-0 bg-slate-950/55 backdrop-blur-[2px]"
                  onClick={onClose}
                  aria-label={t('close')}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.22 }}
                />

                <motion.aside
                  id="header-mobile-menu"
                  className={classNames(
                    'absolute end-0 top-0 flex h-full w-[min(100vw-1.25rem,22rem)] flex-col border-s border-border/80 bg-panel shadow-2xl',
                    panelClassName
                  )}
                  role="dialog"
                  aria-modal="true"
                  aria-label={t('navigation')}
                  initial={{ x: slideOffset }}
                  animate={{ x: 0 }}
                  exit={{ x: slideOffset }}
                  transition={panelTransition}
                >
                  <div className="flex items-center justify-between gap-3 border-b border-border/80 bg-white/40 px-4 py-4 backdrop-blur-xl dark:bg-slate-950/30">
                    <div className="flex min-w-0 items-center gap-3">
                      <SiteLogo size="sm" className="shadow-md" />
                      <div className="min-w-0 text-start">
                        <p className="truncate text-sm font-bold tracking-tight text-text">{title}</p>
                        {subtitle ? <p className="truncate text-xs text-muted">{subtitle}</p> : null}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-white/70 text-muted transition hover:bg-accent-soft hover:text-text dark:bg-slate-950/40"
                      aria-label={t('close')}
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <div className="flex flex-1 flex-col overflow-y-auto p-4">
                    <nav
                      className="flex flex-col gap-1.5 rounded-2xl border border-border/70 bg-white/55 p-1.5 shadow-sm dark:bg-slate-950/25"
                      onClick={onClose}
                    >
                      {wrapWithStagger(children)}
                    </nav>

                    {footer ? (
                      <motion.div
                        className="mt-4 flex flex-col gap-2 rounded-2xl border border-border/70 bg-white/55 p-2 shadow-sm dark:bg-slate-950/25"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.16, duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      >
                        {footer}
                      </motion.div>
                    ) : null}
                  </div>
                </motion.aside>
              </div>
            ) : null}
          </AnimatePresence>,
          document.body
        )
      : null;

  return (
    <>
      {hideTrigger ? null : (
        <button
          type="button"
          className={classNames(
            'inline-flex h-11 w-11 items-center justify-center rounded-full border border-border bg-panel/80 text-text shadow-sm backdrop-blur-xl transition hover:bg-accent-soft',
            overlayClassName,
            open && 'border-accent/30 bg-accent-soft text-accent'
          )}
          onClick={open ? onClose : onOpen}
          aria-expanded={open}
          aria-controls="header-mobile-menu"
          aria-label={open ? t('close') : t('openMenu')}
        >
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span
                key="close-icon"
                initial={{ opacity: 0, rotate: -45, scale: 0.8 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: 45, scale: 0.8 }}
                transition={{ duration: 0.18 }}
                className="inline-flex"
              >
                <X size={20} />
              </motion.span>
            ) : (
              <motion.span
                key="menu-icon"
                initial={{ opacity: 0, rotate: 45, scale: 0.8 }}
                animate={{ opacity: 1, rotate: 0, scale: 1 }}
                exit={{ opacity: 0, rotate: -45, scale: 0.8 }}
                transition={{ duration: 0.18 }}
                className="inline-flex"
              >
                <Menu size={20} />
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      )}

      {menuPortal}
    </>
  );
};
